/**
 * MasterBomComposerPage.tsx
 *
 * Master BOM Composer (soạn thảo theo ma trận Màu × Kết cấu) + Publish chốt phiên bản.
 * Route: /purchasing/boms/:productId/master
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Layers,
  ArrowLeft,
  GitCompare,
  Download,
  Send,
  CheckCircle2,
  AlertTriangle,
  X,
  Sparkles,
} from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import { useCan } from '@/features/auth/AuthContext'
import { productService, type Product } from '@/services/products'
import { catalogService } from '@/services/catalog'
import {
  getMasterBomLinesApi,
  getMasterCombosApi,
  createMasterWoodLineApi,
  updateMasterWoodLineApi,
  deleteMasterWoodLineApi,
  duplicateMasterWoodLineApi,
  createMasterHardwareLineApi,
  updateMasterHardwareLineApi,
  deleteMasterHardwareLineApi,
  duplicateMasterHardwareLineApi,
} from '@/services/boms/masterApiClient'
import type {
  BomMasterWoodLine,
  BomMasterHardwareLine,
  MasterComboRow,
  ApplicableAssemblyOption,
} from '@/services/boms/masterTypes'

import { MasterComboTab } from './MasterComboTab'
import { MasterComposerTab } from './MasterComposerTab'
import { QuickViewModal } from './QuickViewModal'
import { ScopePickerModal } from './ScopePickerModal'
import { PublishModal } from './PublishModal'
import { ImportModal } from './ImportModal'

export function MasterBomComposerPage() {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()

  // RBAC: bom.write required
  const canWrite = useCan('bom.write')

  // Main state
  const [product, setProduct] = useState<Product | null>(null)
  const [combos, setCombos] = useState<MasterComboRow[]>([])
  const [woodLines, setWoodLines] = useState<BomMasterWoodLine[]>([])
  const [hardwareLines, setHardwareLines] = useState<BomMasterHardwareLine[]>([])
  const [woodDuplicateCount, setWoodDuplicateCount] = useState<number>(0)
  const [hardwareDuplicateCount, setHardwareDuplicateCount] = useState<number>(0)
  const [availableColors, setAvailableColors] = useState<
    Array<{ code: string; name: string }>
  >([])

  // UI state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'combos' | 'composer'>('combos')

  // Preview filters
  const [previewColor, setPreviewColor] = useState<string>('ALL')
  const [previewAssembly, setPreviewAssembly] = useState<string>('ALL')

  // Modals state
  const [quickViewCombo, setQuickViewCombo] = useState<MasterComboRow | null>(null)
  const [isPublishOpen, setIsPublishOpen] = useState(false)
  const [publishInitialKey, setPublishInitialKey] = useState<string | null>(null)
  const [isImportOpen, setIsImportOpen] = useState(false)

  const [scopePickerState, setScopePickerState] = useState<{
    isOpen: boolean
    line: BomMasterWoodLine | BomMasterHardwareLine | null
    type: 'wood' | 'hardware'
  }>({
    isOpen: false,
    line: null,
    type: 'wood',
  })

  // Load all data
  const loadData = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    setError(null)

    try {
      // 1. Load product
      const prod = await productService.getById(productId)
      if (!prod) {
        setProduct(null)
        setError('Sản phẩm không tồn tại')
        return
      }
      setProduct(prod)

      // 2. Load combos & master lines in parallel
      const [combosData, linesData, catalogColors] = await Promise.all([
        getMasterCombosApi(productId).catch((e) => {
          console.error('Error fetching combos:', e)
          return [] as MasterComboRow[]
        }),
        getMasterBomLinesApi(productId).catch((e) => {
          console.error('Error fetching lines:', e)
          return {
            wood_lines: [] as BomMasterWoodLine[],
            hardware_lines: [] as BomMasterHardwareLine[],
            wood_duplicate_count: 0,
            hardware_duplicate_count: 0,
          }
        }),
        catalogService.list('color').catch(() => []),
      ])

      setCombos(combosData)
      setWoodLines(linesData.wood_lines)
      setHardwareLines(linesData.hardware_lines)
      setWoodDuplicateCount(linesData.wood_duplicate_count)
      setHardwareDuplicateCount(linesData.hardware_duplicate_count)

      // 3. Compile available colors from catalog + combos
      const colorMap = new Map<string, string>()
      catalogColors.forEach((c) => colorMap.set(c.code, c.name))
      combosData.forEach((cb) => {
        if (!colorMap.has(cb.color_code)) {
          colorMap.set(cb.color_code, cb.color_name)
        }
      })
      const colorList = Array.from(colorMap.entries()).map(([code, name]) => ({
        code,
        name,
      }))
      setAvailableColors(colorList)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Tải dữ liệu thất bại')
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // RBAC block
  if (!canWrite) {
    return (
      <PageNotFound
        title="Không có quyền truy cập"
        description="Màn hình Master BOM (Kỹ thuật) chỉ dành cho tài khoản có quyền kỹ thuật (bom.write)."
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/boms')}>
            Về danh sách BOM
          </Button>
        }
      />
    )
  }

  if (loading) {
    return <PageLoading label="Đang tải Master BOM..." />
  }

  if (error && !product) {
    return (
      <div className="p-6 space-y-4">
        <PageError message={error} onRetry={() => void loadData()} />
        <Button variant="secondary" onClick={() => navigate('/purchasing/boms')}>
          Về danh sách BOM
        </Button>
      </div>
    )
  }

  if (!product) {
    return (
      <PageNotFound
        title="Không tìm thấy sản phẩm"
        description="Sản phẩm không tồn tại hoặc đã bị xóa."
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/boms')}>
            Về danh sách BOM
          </Button>
        }
      />
    )
  }

  // Action handlers
  const handleOpenPublish = (combo: MasterComboRow | null) => {
    if (combo) {
      setPublishInitialKey(`${combo.variant_id}_${combo.assembly_type}`)
    } else {
      setPublishInitialKey(null)
    }
    setIsPublishOpen(true)
  }

  const handlePublishSuccess = (result: any) => {
    setSuccessMessage(
      `Publish thành công! Đã chốt phiên bản ${result.version_label} (v${result.version_seq}) với ${result.wood_line_count} dòng gỗ và ${result.hardware_line_count} dòng ngũ kim.`,
    )
    void loadData()
  }

  const handleImportSuccess = () => {
    setSuccessMessage('Đã nhập thành công các dòng vào Master BOM!')
    setIsImportOpen(false)
    void loadData()
  }

  // Line CRUD handlers — always-on inline cell editing
  const handleUpdateLine = async (
    id: string,
    type: 'wood' | 'hardware',
    patch: Partial<BomMasterWoodLine> & Partial<BomMasterHardwareLine>,
  ) => {
    const prevWood = woodLines
    const prevHardware = hardwareLines

    // Optimistic local update
    if (type === 'wood') {
      setWoodLines((prev) =>
        prev.map((line) => (line.id === id ? { ...line, ...patch } : line)),
      )
    } else {
      setHardwareLines((prev) =>
        prev.map((line) => (line.id === id ? { ...line, ...patch } : line)),
      )
    }

    try {
      if (type === 'wood') {
        await updateMasterWoodLineApi(id, patch)
      } else {
        await updateMasterHardwareLineApi(id, patch)
      }
    } catch (err) {
      setWoodLines(prevWood)
      setHardwareLines(prevHardware)
      setError(err instanceof Error ? err.message : 'Cập nhật dòng thất bại')
    }
  }

  const handleAddLine = async (type: 'wood' | 'hardware') => {
    if (!productId) return
    try {
      if (type === 'wood') {
        await createMasterWoodLineApi(productId, {
          part_name: 'Chi tiết mới',
          qty_per: 1,
          applicable_colors: ['ALL'],
          applicable_assembly: ['ALL'],
        })
      } else {
        await createMasterHardwareLineApi(productId, {
          name_raw: 'Vật tư mới',
          qty_per: 1,
          applicable_colors: ['ALL'],
          applicable_assembly: ['ALL'],
        })
      }
      setSuccessMessage(
        type === 'wood' ? 'Đã thêm dòng gỗ mới' : 'Đã thêm dòng ngũ kim mới',
      )
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Thêm dòng thất bại')
    }
  }

  const handleDuplicateLine = async (id: string, type: 'wood' | 'hardware') => {
    try {
      if (type === 'wood') {
        await duplicateMasterWoodLineApi(id)
      } else {
        await duplicateMasterHardwareLineApi(id)
      }
      setSuccessMessage('Đã nhân đôi dòng thành công')
      void loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nhân đôi dòng thất bại')
    }
  }

  const handleDeleteLine = async (id: string, type: 'wood' | 'hardware') => {
    try {
      if (type === 'wood') {
        await deleteMasterWoodLineApi(id)
      } else {
        await deleteMasterHardwareLineApi(id)
      }
      setSuccessMessage('Đã xóa dòng thành công')
      void loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Xóa dòng thất bại')
    }
  }

  // Scope picker
  const handleOpenScopePicker = (
    line: BomMasterWoodLine | BomMasterHardwareLine,
    type: 'wood' | 'hardware',
  ) => {
    setScopePickerState({
      isOpen: true,
      line,
      type,
    })
  }

  const handleSaveScope = async (
    colors: string[],
    assembly: ApplicableAssemblyOption[],
  ) => {
    const { line, type } = scopePickerState
    if (!line) return
    if (type === 'wood') {
      await updateMasterWoodLineApi(line.id, {
        applicable_colors: colors,
        applicable_assembly: assembly,
      })
    } else {
      await updateMasterHardwareLineApi(line.id, {
        applicable_colors: colors,
        applicable_assembly: assembly,
      })
    }
    setScopePickerState({ isOpen: false, line: null, type: 'wood' })
    void loadData()
  }

  return (
    <div className="space-y-4 pb-12">
      {/* 1. Breadcrumbs */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Định mức (BOM)', to: '/purchasing/boms' },
          { label: product.code, to: `/products/${product.id}` },
          { label: 'Master BOM (Kỹ thuật)' },
        ]}
      />

      {/* 2. Notification alerts */}
      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-3 shadow-sm">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{error}</p>
          </div>
          <button
            type="button"
            className="text-amber-600 hover:text-amber-800"
            onClick={() => setError(null)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-3 shadow-sm">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">{successMessage}</p>
          </div>
          <button
            type="button"
            className="text-emerald-600 hover:text-emerald-800"
            onClick={() => setSuccessMessage(null)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* 3. Page Header */}
      <div className="bg-white p-5 rounded-lg border border-slate-300 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Badge tone="neutral">Master BOM (Kỹ thuật)</Badge>
            <h1 className="text-2xl font-black text-slate-900">{product.code}</h1>
            <span className="text-sm font-medium text-slate-600">({product.name})</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Kích thước: {`${product.w ?? '—'} × ${product.d ?? '—'} × ${product.h ?? '—'} mm`} ·{' '}
            {combos.length} tổ hợp Màu × Kết cấu · Soạn thảo tập trung và Publish chốt phiên bản
          </p>
        </div>

        {/* Top Header Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate(`/products/${product.id}/matrix`)}
            className="flex items-center gap-1.5 text-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Ma trận điều kiện</span>
          </Button>

          <Button
            variant="secondary"
            onClick={() => navigate(`/purchasing/boms/${product.id}/compare`)}
            className="flex items-center gap-1.5 text-xs"
          >
            <GitCompare className="h-3.5 w-3.5" />
            <span>So sánh BOM</span>
          </Button>

          <Button
            variant="secondary"
            onClick={() => setIsImportOpen(true)}
            className="flex items-center gap-1.5 text-xs"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Nhập từ BOM cũ</span>
          </Button>

          <Button
            variant="primary"
            onClick={() => handleOpenPublish(null)}
            className="flex items-center gap-1.5 text-xs"
          >
            <Send className="h-3.5 w-3.5" />
            <span>Publish BOM</span>
          </Button>
        </div>
      </div>

      {/* 4. Tab Navigation */}
      <div className="flex border-b border-slate-300">
        <button
          type="button"
          onClick={() => setActiveTab('combos')}
          className={`py-3 px-5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'combos'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Layers className="h-4 w-4" />
          <span>Bảng tổ hợp (Màu × Kết cấu)</span>
          <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-normal">
            {combos.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('composer')}
          className={`py-3 px-5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'composer'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Sparkles className="h-4 w-4" />
          <span>Master BOM (Soạn thảo)</span>
          <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-normal">
            {woodLines.length + hardwareLines.length}
          </span>
          {(woodDuplicateCount > 0 || hardwareDuplicateCount > 0) && (
            <span className="h-2 w-2 rounded-full bg-amber-500" title="Có dòng trùng 100%" />
          )}
        </button>
      </div>

      {/* 5. Tab Content */}
      {activeTab === 'combos' ? (
        <MasterComboTab
          combos={combos}
          loading={loading}
          onQuickView={(c) => setQuickViewCombo(c)}
          onPublishClick={(c) => handleOpenPublish(c)}
          onSwitchToComposerWithPreview={(cCode, asm) => {
            setPreviewColor(cCode)
            setPreviewAssembly(asm)
            setActiveTab('composer')
          }}
        />
      ) : (
        <MasterComposerTab
          woodLines={woodLines}
          hardwareLines={hardwareLines}
          woodDuplicateCount={woodDuplicateCount}
          hardwareDuplicateCount={hardwareDuplicateCount}
          availableColors={availableColors}
          onAddLine={handleAddLine}
          onUpdateLine={handleUpdateLine}
          onDuplicateLine={handleDuplicateLine}
          onDeleteLine={handleDeleteLine}
          onOpenScopePicker={handleOpenScopePicker}
          previewColor={previewColor}
          previewAssembly={previewAssembly}
          onChangePreviewColor={setPreviewColor}
          onChangePreviewAssembly={setPreviewAssembly}
        />
      )}

      {/* 6. Modals */}
      {/* Quick View Modal */}
      <QuickViewModal
        isOpen={Boolean(quickViewCombo)}
        onClose={() => setQuickViewCombo(null)}
        combo={quickViewCombo}
        woodLines={woodLines}
        hardwareLines={hardwareLines}
      />

      {/* Scope Picker Modal */}
      <ScopePickerModal
        isOpen={scopePickerState.isOpen}
        onClose={() => setScopePickerState({ isOpen: false, line: null, type: 'wood' })}
        onSave={handleSaveScope}
        initialColors={scopePickerState.line?.applicable_colors || ['ALL']}
        initialAssembly={scopePickerState.line?.applicable_assembly || ['ALL']}
        availableColors={availableColors}
        lineName={
          scopePickerState.type === 'wood'
            ? (scopePickerState.line as BomMasterWoodLine)?.part_name || ''
            : (scopePickerState.line as BomMasterHardwareLine)?.name_raw || ''
        }
        lineType={scopePickerState.type}
      />

      {/* Publish Modal */}
      <PublishModal
        isOpen={isPublishOpen}
        onClose={() => setIsPublishOpen(false)}
        combos={combos}
        initialComboKey={publishInitialKey}
        productId={product.id}
        onPublishSuccess={handlePublishSuccess}
      />

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        combos={combos}
        hasExistingMasterLines={woodLines.length > 0 || hardwareLines.length > 0}
        productId={product.id}
        onImportSuccess={handleImportSuccess}
      />
    </div>
  )
}
